// frontend/src/pages/CoachHandbookPage.js
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import coachHandbookApi from "../services/coachHandbookApi";
import { getChapterProgress } from "../utils/handbookProgress";
import LoadingSpinner from "../components/common/LoadingSpinner";
import HandbookHeader from "../components/handbook/HandbookHeader";
import HandbookSidebar from "../components/handbook/HandbookSidebar";
import HandbookOverview from "../components/handbook/HandbookOverview";
import HandbookChapter from "../components/handbook/HandbookChapter";
import HandbookSearch from "../components/handbook/HandbookSearch";
import HandbookMobileNav from "../components/handbook/HandbookMobileNav";
import HandbookTOC from "../components/handbook/HandbookTOC";

const CoachHandbookPage = () => {
  const { chapterSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isCoach, loading: authLoading } = useAuth();

  const [handbook, setHandbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setProgressTick] = useState(0);

  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate(
        `/login?returnUrl=${encodeURIComponent(window.location.pathname)}`,
        { replace: true }
      );
      return;
    }

    if (!isCoach) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await coachHandbookApi.getHandbook();
        if (!cancelled) {
          setHandbook(response.data || response);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              "Coach Handbook konnte nicht geladen werden."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isCoach, authLoading, navigate]);

  // Build a flat list for prev/next navigation
  const flatChapters = useMemo(() => {
    if (!handbook?.sections) return [];
    return handbook.sections.flatMap((s) =>
      s.chapters.map((c) => ({ ...c, section_title: s.title }))
    );
  }, [handbook]);

  const currentChapter = useMemo(() => {
    if (!chapterSlug || !flatChapters.length) return null;
    return flatChapters.find((c) => c.slug === chapterSlug) || null;
  }, [chapterSlug, flatChapters]);

  const { prevChapter, nextChapter } = useMemo(() => {
    if (!currentChapter) return { prevChapter: null, nextChapter: null };
    const idx = flatChapters.findIndex((c) => c.slug === currentChapter.slug);
    return {
      prevChapter: idx > 0 ? flatChapters[idx - 1] : null,
      nextChapter:
        idx < flatChapters.length - 1 ? flatChapters[idx + 1] : null,
    };
  }, [currentChapter, flatChapters]);

  const progressForCurrent = useMemo(() => {
    if (!currentChapter) return null;
    return getChapterProgress(currentChapter.slug, currentChapter.checklists);
  }, [currentChapter]);

  const handleProgressChange = () => setProgressTick((n) => n + 1);

  if (authLoading || loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isCoach) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-12 text-center">
        <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-[#002855] mb-2">
          Nur für Coaches
        </h1>
        <p className="text-gray-600 mb-6">
          Das Coach Handbook ist nur für freigeschaltete burnair Coaches
          verfügbar.
        </p>
        <Link
          to="/"
          className="inline-block px-4 py-2 bg-[#002855] text-white rounded-lg hover:bg-[#00A3E0] transition-colors"
        >
          Zurück zur Startseite
        </Link>
      </main>
    );
  }

  if (error) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-[#002855] mb-2">
          Fehler beim Laden
        </h1>
        <p className="text-gray-600">{error}</p>
      </main>
    );
  }

  if (!handbook) return null;

  return (
    <div className="bg-gray-50 min-h-[calc(100vh-4rem)]">
      <HandbookHeader
        handbook={handbook}
        currentChapter={currentChapter}
        progressForCurrent={progressForCurrent}
        onSearchClick={() => setSearchOpen(true)}
        onMobileMenuClick={() => setMobileNavOpen(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex gap-6 md:gap-8">
          <HandbookSidebar handbook={handbook} />

          <div className="flex-1 min-w-0">
            {currentChapter ? (
              <HandbookChapter
                chapter={currentChapter}
                prevChapter={prevChapter}
                nextChapter={nextChapter}
                onProgressChange={handleProgressChange}
              />
            ) : (
              <HandbookOverview handbook={handbook} />
            )}
          </div>

          {currentChapter && <HandbookTOC chapter={currentChapter} />}
        </div>
      </main>

      <HandbookSearch
        handbook={handbook}
        isOpen={searchOpen}
        onOpen={() => setSearchOpen(true)}
        onClose={() => setSearchOpen(false)}
      />

      <HandbookMobileNav
        handbook={handbook}
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
    </div>
  );
};

export default CoachHandbookPage;
